import { type PaginationState, TablePagination } from '@/components/shared/TablePagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface ContractsTableColumn<T> {
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  headerClassName?: string;
  cellClassName?: string;
}

interface ContractsTableProps<T> {
  columns: ContractsTableColumn<T>[];
  data: T[];
  rowKey: (row: T) => string;
  emptyMessage?: string;
  isLoading?: boolean;
  loadingRowCount?: number;
  pagination?: PaginationState;
  onPageChange?: (page: number) => void;
  showingLabel?: string;
}

const ContractsTable = <T,>({
  columns,
  data,
  rowKey,
  emptyMessage,
  isLoading = false,
  loadingRowCount = 5,
  pagination,
  onPageChange,
  showingLabel,
}: ContractsTableProps<T>) => {
  const { t } = useTranslation();
  const skeletonRows = Math.max(loadingRowCount, 1);

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/30 hover:bg-muted/30">
            {columns.map((column) => (
              <TableHead
                key={column.id}
                className={cn(
                  'h-12 text-sm font-semibold text-[hsl(var(--fg))]',
                  column.headerClassName
                )}
              >
                {column.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>

        <TableBody>
          {isLoading ? (
            Array.from({ length: skeletonRows }).map((_, rowIndex) => (
              <TableRow
                key={`contracts-table-skeleton-${rowIndex}`}
                className="hover:bg-transparent"
              >
                {columns.map((column, columnIndex) => (
                  <TableCell
                    key={`${column.id}-skeleton`}
                    className={cn('py-4', column.cellClassName)}
                  >
                    <Skeleton
                      className={cn(
                        'h-4 rounded-full',
                        columnIndex === 0 ? 'w-3/4' : 'w-2/3',
                        columnIndex === columns.length - 1 ? 'ml-auto w-20' : ''
                      )}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : data.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell
                colSpan={columns.length}
                className="py-10 text-center text-muted-foreground"
              >
                {emptyMessage ?? t('ORGCONTRACTS_EMPTY')}
              </TableCell>
            </TableRow>
          ) : (
            data.map((row) => (
              <TableRow key={rowKey(row)} className="hover:bg-transparent">
                {columns.map((column) => (
                  <TableCell key={column.id} className={cn('py-4', column.cellClassName)}>
                    {column.cell(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {!isLoading && pagination && onPageChange ? (
        <div className="border-t border-border bg-background px-4 py-2">
          <TablePagination
            pagination={pagination}
            onPageChange={onPageChange}
            showingLabel={showingLabel}
          />
        </div>
      ) : null}
    </div>
  );
};

export default ContractsTable;
